import { useMemo } from 'react';
import type {
  AgentApiAnalysisSharedChart,
  AgentApiAnalysisSharedSummaryItem,
} from '@/types/serializers';
import { TABLE_STEPS } from '../constants';

/**
 * チャート1ステップ分の行データ。
 * - step: X軸ラベル（日本語）
 * - total: summary 由来の値（ユニーク集計、サマリーカードと一致）
 * - seriesSum: chart.series の値合計（バーの実視覚高さに一致。groupBy 時は担当者重複で total を超え得る）
 * - delta: 上端ラベルの色分け用（前期比、summary 由来）
 * - 残りは series.key → 値 の動的キー（積み上げバー用）
 */
export interface FunnelChartRow {
  step: string;
  total: number;
  seriesSum: number;
  delta: number;
  [seriesKey: string]: string | number;
}

/**
 * AnalysisFunnelChart のチャートデータを構築するカスタムフック。
 * - visibleSteps（API キー配列）で TABLE_STEPS をフィルタして表示順を確定
 * - summary から total / delta を、chart.series から系列ごとの値を統合
 */
export function useFunnelChartData(
  summary: AgentApiAnalysisSharedSummaryItem[],
  chart: AgentApiAnalysisSharedChart,
  visibleSteps: string[]
): FunnelChartRow[] {
  return useMemo(() => {
    const displayStepDefs = TABLE_STEPS.filter((s) => visibleSteps.includes(s.key));
    const summaryMap = new Map(summary.map((s) => [s.step, s]));

    return displayStepDefs.map((stepDef) => {
      const item = summaryMap.get(stepDef.key);
      const stepIndex = chart.steps.indexOf(stepDef.key);
      const seriesValues: Record<string, number> = {};
      let seriesSum = 0;
      chart.series.forEach((series) => {
        const v = stepIndex >= 0 ? (series.values[stepIndex] ?? 0) : 0;
        seriesValues[series.key] = v;
        seriesSum += v;
      });
      return {
        step: stepDef.label,
        total: item?.current_value ?? 0,
        seriesSum,
        delta: item?.delta ?? 0,
        ...seriesValues,
      };
    });
  }, [summary, chart, visibleSteps]);
}
